import { ReactNode, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { api } from '@/api/repository/axios';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import ImageCropModal from '@/components/common/organism/ImageCropModal';
import { ImageTooLargeError } from '@/utils/compressImage';
import { FORM_COLOR as C } from '@/pages/Seminar/components/form/formStyles';
import type { HomepagePhotoController } from './useHomepagePhotoAuto';
import { HOMEPAGE_PHOTO_FULL_MESSAGE } from './useHomepagePhotoAuto';
import {
    addPhoto,
    canAddPhoto,
    DEFAULT_CLINIC_IMAGE,
    isHomepagePhoto,
    makeMain,
    MAX_CLINIC_IMAGES,
    overLimitCount,
    realPhotoCount,
    removePhoto,
    replaceMain,
} from './homepagePhotoRules';

/**
 * 대표 사진 + 치과 이미지(5장까지) — 가입 2단계·치과 정보 수정이 같이 쓴다(2026-10-26 승인 시안 + 리뷰 반영).
 * 순서: 대표 사진 → 치과 이미지 n/5 + 추가 사진 타일 → 버튼 줄. 홈페이지 사진은 엔진이 목록에 바로 넣는다(2026-10-30 '더 넣기' 줄 없앰).
 * - 대표: 16:9, 꼬리표 "홈페이지 사진"(홈페이지에서 저장한 주소)·"기본 이미지". 0장이면 점선 칸을 눌러 올린다.
 * - 사진 타일 목록에 대표까지 모두(2026-10-30) — 대표 타일은 고른 표시, [×]로 빼면 다음 사진이 대표, 없으면 기본 이미지(기본 이미지엔 × 없음).
 * - 추가 사진: 폰 3열·PC 4열 타일, 누르면 대표로, 바깥 모서리 [×] 로 빼기. [사진 추가]는 직접 올려 맨 뒤에(5장이면 숨김).
 * - 올리기는 [사진 추가] 하나, 대표 바꾸기는 추가 사진 누르기(2026-10-30 [직접 올리기]·[다른 사진으로 바꾸기] 없앰).
 *   기본 이미지·0장이면 버튼 줄의 [사진 추가]가 대표 자리에 넣는다.
 * 상태·읽기는 [useHomepagePhotoAuto] 가 갖고, 이 부품은 그리기와 직접 올리기만 한다.
 */
interface HomepagePhotoFieldProps {
    photo: HomepagePhotoController;
    /** 대표 사진 칸 오류(가입 필수 확인) */
    hasError?: boolean;
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    /* 폰은 440 안에서 3열, PC는 사진 목록이 한 줄로 늘어서게 폭 제한 없음(대표 칸은 MainTile 360) — 2026-10-30 */
    @media (max-width: 768px) {
        max-width: 440px;
    }
`;

const MainTile = styled.div<{ hasError?: boolean }>`
    position: relative;
    width: 100%;
    max-width: 360px;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid ${(p) => (p.hasError ? C.danger : C.line)};
    background: ${C.lineSoft};

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

/** 0장 — 같은 16:9 자리를 눌러 대표 사진을 올린다 */
const MainPlaceholder = styled.button<{ hasError?: boolean }>`
    width: 100%;
    max-width: 360px;
    aspect-ratio: 16 / 9;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0;
    border: 1.5px dashed ${(p) => (p.hasError ? C.danger : C.border)};
    border-radius: 12px;
    background: ${C.primaryCard};
    color: ${C.primaryDeep};
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    svg {
        display: block;
        width: 28px;
        height: 28px;
    }

    &:disabled {
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${C.primaryDeep};
        outline-offset: -2px;
    }
`;

/** 대표 사진 위 꼬리표 */
const Tag = styled.span`
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 2px 8px;
    border-radius: 6px;
    background: ${C.primaryDeep};
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
`;

/** 읽는 중 — 기본 이미지·자동 사진 위에만 덮는 띠(직접 올린 대표에는 띄우지 않는다) */
const LoadingBar = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
`;

/** 직접 올린 대표일 때 읽는 중 — 대표 위 띠 대신 버튼 줄 아래 */
const LoadingLine = styled.p`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 13px;
    color: ${C.sub};
`;

const ButtonRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    @media (max-width: 768px) {
        flex-wrap: nowrap;

        > button {
            flex: 1 1 0;
            min-width: 0;
        }
    }
`;

/** 보조 버튼 — 같은 화면의 보조 버튼(테두리 #D3DBD8·반경 10)과 같은 모양, 높이 44. 처리 중엔 글자 자리를 남기고 스피너를 겹친다 */
const GhostButton = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    padding: 0 16px;
    border: 1px solid ${C.border};
    border-radius: 10px;
    background: #fff;
    color: ${C.primaryDeep};
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${C.primaryDeep};
        outline-offset: -2px;
    }
`;

const HiddenLabel = styled.span<{ concealed?: boolean }>`
    visibility: ${(p) => (p.concealed ? 'hidden' : 'visible')};
`;

const OverlaySpinner = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

/** 버튼 글자 — 처리 중이면 글자는 숨겨 폭을 지키고 스피너를 겹친다 */
const ButtonContent = ({ busy, children }: { busy: boolean; children: ReactNode }) => (
    <>
        <HiddenLabel concealed={busy} aria-hidden={busy || undefined}>{children}</HiddenLabel>
        {busy && <OverlaySpinner><ButtonSpinner /></OverlaySpinner>}
    </>
);

const Note = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${C.sub};
    word-break: keep-all;
`;

const ErrorNotice = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${C.danger};
    word-break: keep-all;
`;

/** 작은 타일 그리드 — 폰 3열·PC 4열(HomepagePhotoPicker 와 같음). [×]가 바깥 모서리로 나오는 만큼 위·오른쪽을 비운다 */
const TileGrid = styled.div<{ withRemove?: boolean }>`
    display: grid;
    /* PC — 모든 사진을 한 줄로(칸 96px, 폭이 모자라면 같이 줄어든다 — 가입 폼처럼 좁은 곳도 한 줄) */
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 96px);
    justify-content: start;
    gap: ${(p) => (p.withRemove ? '12px' : '8px')};
    padding: ${(p) => (p.withRemove ? '12px 12px 0 0' : '0')};

    @media (max-width: 768px) {
        grid-auto-flow: row;
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
`;

/** 추가 사진 한 칸 — 사진 버튼(누르면 대표로) + 바깥 모서리 [×] */
const SmallItem = styled.div`
    position: relative;
`;

const TileImageButton = styled.button`
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    padding: 0;
    border: 1px solid ${C.line};
    border-radius: 8px;
    overflow: hidden;
    background: ${C.lineSoft};
    cursor: pointer;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus-visible {
        outline: 2px solid ${C.primaryDeep};
        outline-offset: -2px;
    }
`;

/** [×] — 타일 오른쪽 위 바깥 모서리. 누르는 영역 32px(사진 버튼보다 위에 놓여 겹친 곳은 [×]), 보이는 원 22px + 16px SVG 가운데 */
const RemoveButton = styled.button`
    position: absolute;
    top: -12px;
    right: -12px;
    z-index: 1;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;

    > span {
        width: 22px;
        height: 22px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.55);
        color: #fff;
    }

    svg {
        display: block;
        width: 16px;
        height: 16px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus-visible {
        outline: none;
    }

    &:focus-visible > span {
        outline: 2px solid ${C.primaryDeep};
        outline-offset: 1px;
    }
`;

/** 목록 안 대표 타일 — 청록 2px 테두리 + 왼쪽 위 '대표' 꼬리표 */
const SelectedTileButton = styled(TileImageButton)`
    position: relative;
    border: 2px solid ${C.primary};
    cursor: default;
`;

/** 대표 꼬리표 — 대표 사진 칸의 꼬리표(Tag)와 같은 색, 작은 타일에 맞춘 크기. [×]가 오른쪽 위라 왼쪽 위에 */
const MainBadge = styled.span`
    position: absolute;
    top: 4px;
    left: 4px;
    padding: 2px 6px;
    border-radius: 6px;
    background: ${C.primaryDeep};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 16px;
`;

/** [사진 추가] — 직접 올려 맨 뒤에(작은 점선 타일) */
const AddTile = styled.button`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    min-height: 44px;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 0;
    border: 1.5px dashed ${C.border};
    border-radius: 8px;
    background: ${C.primaryCard};
    color: ${C.primaryDeep};
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    svg {
        display: block;
        width: 18px;
        height: 18px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus-visible {
        outline: 2px solid ${C.primaryDeep};
        outline-offset: -2px;
    }
`;

const CountLine = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${C.muted};
`;

/** 한도 문구 — 빨강(UI 가이드 빨강 한 가지) */
const LimitText = styled.span`
    color: ${C.danger};
    font-weight: 600;
`;

/** 추가 사진 위 짧은 안내 */
const Hint = styled.p`
    margin: -4px 0 0;
    font-size: 13px;
    color: ${C.muted};
`;

type UploadTarget = 'main' | 'add';

const HomepagePhotoField = ({ photo, hasError = false }: HomepagePhotoFieldProps) => {
    const { state, modify, loading, pickingCandidate, savedByCandidate, photoNotice, setPhotoNotice, fieldMessage } = photo;
    const inputRef = useRef<HTMLInputElement>(null);
    const targetRef = useRef<UploadTarget>('main');
    const [cropSource, setCropSource] = useState<File | null>(null);
    const [uploading, setUploading] = useState<UploadTarget | null>(null);

    const main: string | undefined = state.images[0];
    const isDefaultOrEmpty: boolean = !main || main === DEFAULT_CLINIC_IMAGE;
    // 사진 자리 고정(2026-10-30) — 대표를 바꿔도 타일 순서는 그대로, 새 사진은 뒤에, 뺀 사진은 빠진다(저장 순서는 대표가 [0])
    const orderRef = useRef<string[]>([]);
    const realImages: string[] = isDefaultOrEmpty ? [] : state.images;
    const order: string[] = [
        ...orderRef.current.filter((url) => realImages.includes(url)),
        ...realImages.filter((url) => !orderRef.current.includes(url)),
    ];
    orderRef.current = order;
    let extraNo: number = 0;
    const count: number = realPhotoCount(state.images);
    const full: boolean = !canAddPhoto(state);
    /** 저장하려면 빼야 하는 장수 */
    const over: number = overLimitCount(state);
    /** 직접 고치기(올리기·빼기·대표 바꾸기)를 막는 동안 */
    const editBusy: boolean = !!uploading || !!pickingCandidate;
    const manualMain: boolean = state.mainSource === 'manual' && !isDefaultOrEmpty;

    const openUpload = (target: UploadTarget): void => {
        if (editBusy) return;
        if (target === 'add' && full) {
            setPhotoNotice(HOMEPAGE_PHOTO_FULL_MESSAGE, 'info');
            return;
        }
        targetRef.current = target;
        inputRef.current?.click();
    };

    const onCropped = async (cropped: File): Promise<void> => {
        setCropSource(null);
        const target: UploadTarget = targetRef.current;
        setUploading(target);
        try {
            const res = await api.upload.image(cropped);
            const url: string | undefined = res.code === 0 ? res.result?.url : undefined;
            if (!url) {
                setPhotoNotice('이미지 업로드에 실패했습니다.', 'error');
                return;
            }
            if (target === 'main') {
                modify((current) => replaceMain(current, url, 'manual'));
            } else if (!modify((current) => addPhoto(current, url))) {
                setPhotoNotice(HOMEPAGE_PHOTO_FULL_MESSAGE, 'info');
            }
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            setPhotoNotice(error instanceof ImageTooLargeError ? error.message : '이미지 업로드에 실패했습니다.', 'error');
        } finally {
            setUploading(null);
        }
    };

    return (
        <Wrap>
            {/* 직접 올리기 공용 숨김 파일 입력 → 16:9 크롭 모달 */}
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) {
                        setPhotoNotice('파일 크기가 10MB를 초과합니다.', 'error');
                        return;
                    }
                    setCropSource(file);
                }}
            />
            <ImageCropModal file={cropSource} onCancel={() => setCropSource(null)} onCropped={onCropped} />

            {/* ① 대표 사진 */}
            {main ? (
                <MainTile hasError={hasError}>
                    <img src={main} alt="대표 사진" referrerPolicy="no-referrer" />
                    {isHomepagePhoto(main, savedByCandidate) && <Tag>홈페이지 사진</Tag>}
                    {main === DEFAULT_CLINIC_IMAGE && <Tag>기본 이미지</Tag>}
                    {((loading && !manualMain) || uploading === 'main') && (
                        <LoadingBar role="status">
                            <span aria-hidden><ButtonSpinner /></span>
                            {uploading === 'main' ? '올리는 중…' : '홈페이지 사진 찾는 중…'}
                        </LoadingBar>
                    )}
                </MainTile>
            ) : (
                <MainPlaceholder type="button" hasError={hasError} onClick={() => openUpload('main')} disabled={editBusy || loading}>
                    {loading || uploading === 'main' ? (
                        <>
                            <span aria-hidden><ButtonSpinner /></span>
                            <span role="status">{loading ? '홈페이지 사진 찾는 중…' : '올리는 중…'}</span>
                        </>
                    ) : (
                        <>
                            <AddPhotoAlternateOutlinedIcon aria-hidden />
                            대표 사진을 올려 주세요
                        </>
                    )}
                </MainPlaceholder>
            )}
            {fieldMessage && <Note role="status">{fieldMessage}</Note>}

            {/* ② 치과 이미지 n/5 + 모든 사진 타일(대표 포함 — 대표는 고른 표시, 누르면 대표, 2026-10-30) — 기본 이미지·0장이면 숨김 */}
            {count > 0 && (
                <>
                    {/* 5장을 넘어도 모두 보여 주고 저장할 때 5장으로 고른다 — 넘었을 때만 빨간 한도·뺄 장수(2026-10-30) */}
                    <CountLine>
                        {`치과 이미지 ${count}/${MAX_CLINIC_IMAGES}`}
                        {over > 0 && <LimitText>{` · ${MAX_CLINIC_IMAGES}장까지 선택 가능 — ${over}장을 빼 주세요`}</LimitText>}
                    </CountLine>
                    {count > 1 && <Hint>사진을 누르면 대표 사진이 돼요.</Hint>}
                    <TileGrid withRemove role="list" aria-label="사진 목록">
                        {order.map((url) => {
                            if (url === main) {
                                return (
                                    <SmallItem key={url} role="listitem">
                                        <SelectedTileButton type="button" aria-label="대표 사진(선택됨)" aria-pressed disabled={editBusy}>
                                            <img src={url} alt="" referrerPolicy="no-referrer" />
                                            <MainBadge aria-hidden>대표</MainBadge>
                                        </SelectedTileButton>
                                        <RemoveButton type="button" aria-label="대표 사진 빼기" onClick={() => modify((current) => removePhoto(current, 0))} disabled={editBusy}>
                                            <span><CloseRoundedIcon aria-hidden /></span>
                                        </RemoveButton>
                                    </SmallItem>
                                );
                            }
                            extraNo += 1;
                            return (
                                <SmallItem key={url} role="listitem">
                                    <TileImageButton type="button" aria-label={`추가 사진 ${extraNo} 대표 사진으로`} onClick={() => modify((current) => makeMain(current, current.images.indexOf(url)))} disabled={editBusy}>
                                        <img src={url} alt="" referrerPolicy="no-referrer" />
                                    </TileImageButton>
                                    <RemoveButton type="button" aria-label={`추가 사진 ${extraNo} 빼기`} onClick={() => modify((current) => removePhoto(current, current.images.indexOf(url)))} disabled={editBusy}>
                                        <span><CloseRoundedIcon aria-hidden /></span>
                                    </RemoveButton>
                                </SmallItem>
                            );
                        })}
                        {!full && (
                        <SmallItem role="listitem">
                            <AddTile type="button" onClick={() => openUpload('add')} disabled={editBusy} aria-label="사진 추가(직접 올리기)">
                                {uploading === 'add'
                                    ? <span aria-hidden><ButtonSpinner /></span>
                                    : <><AddPhotoAlternateOutlinedIcon aria-hidden />사진 추가</>}
                            </AddTile>
                        </SmallItem>
                        )}
                    </TileGrid>
                </>
            )}

            {/* ③ 기본 이미지·0장 — 추가 사진 타일 줄이 없으니 [사진 추가]를 여기에(대표 자리로 들어간다). 대표 바꾸기는 추가 사진을 눌러서(2026-10-30 [다른 사진으로 바꾸기] 없앰) */}
            {isDefaultOrEmpty && !!main && (
                <ButtonRow>
                    <GhostButton type="button" onClick={() => openUpload('add')} disabled={editBusy}>
                        <ButtonContent busy={uploading === 'add'}>사진 추가</ButtonContent>
                    </GhostButton>
                </ButtonRow>
            )}
            {main === DEFAULT_CLINIC_IMAGE && !loading && <Note>우리 치과 사진을 올리면 공고에 더 잘 보여요.</Note>}


            {/* ④ 직접 올린 대표일 때 읽는 중 표시 — 대표 위 띠 대신 이 자리(홈페이지 사진은 '더 넣기' 줄 없이 목록에 바로 들어간다, 2026-10-30) */}
            {loading && manualMain && (
                <LoadingLine role="status">
                    <span aria-hidden><ButtonSpinner /></span>
                    홈페이지 사진 찾는 중…
                </LoadingLine>
            )}
            {photoNotice && (photoNotice.tone === 'error'
                ? <ErrorNotice role="alert">{photoNotice.text}</ErrorNotice>
                : <Note role="status">{photoNotice.text}</Note>)}
        </Wrap>
    );
};

export default HomepagePhotoField;
